/**
 * Provenance Actions
 *
 * Multi-step flow for retire, supersede, split, merge, and fork.
 *
 * Step 1 — Choose action + configure successors/titles
 * Step 2 — Resolve relationships (per canon 07B §3; skipped for fork + items with no active rels)
 * Step 3 — Confirm + rationale
 *
 * Per canon 07B, the resolution step shows every active relationship on the
 * source item with its recommended disposition (dissolve / transfer) and lets
 * the operator override. Blocs are hard-blocked and must be dissolved before
 * the modal can advance past step 1.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  X,
  Archive,
  ArrowRight,
  GitMerge,
  GitBranch,
  Scissors,
  AlertCircle,
  Plus,
  Trash2,
  Loader2,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Ban,
  ArrowRightLeft,
  MinusCircle,
  Info,
} from 'lucide-react';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { SearchableItemPicker } from '../SearchableItemPicker';
import { generateUUID } from '../../../lib/supabaseSync';
import {
  computeResolutionItems,
  applyResolutions,
  type ResolutionItem,
  type LineageAction,
  type DispositionCode,
} from '../../../lib/lineageResolutionService';

// ============================================================================
// Action metadata
// ============================================================================

const ACTION_META: {
  value: LineageAction;
  label: string;
  description: string;
  icon: React.ElementType;
}[] = [
  { value: 'retire',    label: 'Retire',    description: 'Close this item with no successor',                       icon: Archive    },
  { value: 'supersede', label: 'Supersede', description: 'Replace with another existing item',                      icon: ArrowRight  },
  { value: 'split',     label: 'Split',     description: 'Break into two or more new items — original is retired',  icon: Scissors   },
  { value: 'merge',     label: 'Merge',     description: 'Combine with other items into one — all originals retired',icon: GitMerge   },
  { value: 'fork',      label: 'Fork',      description: 'Create a variant — original continues alongside the fork',icon: GitBranch  },
];

// ============================================================================
// Step indicator
// ============================================================================

function StepIndicator({
  currentStep,
  totalSteps,
  stepLabels,
}: {
  currentStep: number;
  totalSteps: number;
  stepLabels: string[];
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-1 gap-y-2 min-w-0">
      {stepLabels.slice(0, totalSteps).map((label, i) => {
        const step = i + 1;
        const done = step < currentStep;
        const active = step === currentStep;
        return (
          <div key={step} className="flex items-center min-w-0">
            <div className="flex items-center gap-1 min-w-0">
              <div
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-semibold flex-shrink-0 transition-colors ${
                  done  ? 'bg-[var(--slate)] text-white' :
                  active ? 'bg-[var(--slate-dim)] border-2 border-[var(--slate)] text-[var(--text-sec)]' :
                  'bg-[var(--hover-tint)] border-2 border-[var(--rule)] text-[var(--text-ter)]'
                }`}
              >
                {done ? <CheckCircle2 className="h-3 w-3" /> : step}
              </div>
              <span className={`text-[11px] font-medium whitespace-nowrap ${
                active ? 'text-[var(--text-ter)]' : done ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'
              }`}>
                {label}
              </span>
            </div>
            {i < totalSteps - 1 && (
              <div className={`mx-1.5 h-px w-4 flex-shrink-0 ${done ? 'bg-[var(--slate)]' : 'bg-[var(--hover-tint)]'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Resolution item row
// ============================================================================

function ResolutionRow({
  item,
  onChange,
}: {
  item: ResolutionItem;
  onChange: (id: string, disposition: DispositionCode) => void;
}) {
  const getItemById = useItemsStore((s) => s.getItemById);

  const otherItemTitles = item.otherItemIds.map((id) => {
    return getItemById(id)?.title ?? id.slice(0, 12) + '…';
  });

  const isDecided = item.disposition !== null;
  const isBlocking = item.isBlocking && item.defaultDisposition === null;

  return (
    <div className={` border p-3.5 space-y-3 ${
      isBlocking
        ? 'border-[var(--coral)] bg-[var(--coral-dim)]'
        : isDecided
        ? 'border-[var(--rule)] bg-[var(--bg-raised)]'
        : 'border-[var(--mustard)] bg-[var(--mustard-dim)]/50'
    }`}>
      {/* Header row */}
      <div className="flex items-start gap-2.5">
        <div className={`flex-shrink-0 mt-0.5 ${isBlocking ? 'text-[var(--coral)]' : 'text-[var(--text-ter)]'}`}>
          {isBlocking ? <Ban className="h-4 w-4" /> : <Info className="h-4 w-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-[var(--text-pri)] uppercase tracking-wide">
              {item.typeLabel}
            </span>
            {isBlocking && (
              <span className="text-xs font-medium text-[var(--coral)] bg-[var(--coral-dim)] px-1.5 py-0.5 rounded">
                Must dissolve first
              </span>
            )}
          </div>
          <p className="text-sm text-[var(--text-sec)] mt-0.5">{item.roleLabel}</p>
          {otherItemTitles.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {otherItemTitles.map((title, i) => (
                <span key={i} className="text-xs px-2 py-0.5 bg-[var(--hover-tint)] text-[var(--text-sec)] rounded-full">
                  {title}
                </span>
              ))}
            </div>
          )}
          <p className="text-xs text-[var(--text-ter)] mt-1.5 italic">{item.note}</p>
        </div>
      </div>

      {/* Disposition selector — only when NOT a hard blocker */}
      {!isBlocking && (
        <div className="flex items-center gap-2 pl-6">
          <span className="text-xs text-[var(--text-ter)] font-medium mr-1">Disposition:</span>
          <button
            type="button"
            onClick={() => onChange(item.id, 'dissolve')}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1  text-xs font-medium transition-colors border ${
              item.disposition === 'dissolve'
                ? 'bg-[var(--coral-dim)] border-[var(--coral)] text-[var(--coral)]'
                : 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-sec)] hover:border-[var(--rule)]'
            }`}
          >
            <MinusCircle className="h-3 w-3" />
            Dissolve
          </button>
          {item.canTransfer && (
            <button
              type="button"
              onClick={() => onChange(item.id, 'transfer')}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1  text-xs font-medium transition-colors border ${
                item.disposition === 'transfer'
                  ? 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]'
                  : 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-sec)] hover:border-[var(--rule)]'
              }`}
            >
              <ArrowRightLeft className="h-3 w-3" />
              Transfer to successor
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main modal
// ============================================================================

interface ProvenanceActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
  sourceItemId: string;
  /** Which action the dialog opens on. Defaults to retire. */
  initialAction?: LineageAction;
}

export function ProvenanceActionModal({
  isOpen,
  onClose,
  onComplete,
  sourceItemId,
  initialAction = 'retire',
}: ProvenanceActionModalProps) {
  const retireItem     = useItemsStore((s) => s.retireItem);
  const supersedeItem  = useItemsStore((s) => s.supersedeItem);
  const splitItem      = useItemsStore((s) => s.splitItem);
  const forkItem       = useItemsStore((s) => s.forkItem);
  const mergeItems     = useItemsStore((s) => s.mergeItems);
  const getItemById    = useItemsStore((s) => s.getItemById);
  const fetchItems     = useItemsStore((s) => s.fetchFromSupabase);
  const createClosureEvent   = useItemRelationshipsStore((s) => s.createClosureEvent);
  const fetchFromSupabase    = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  const allGroups      = useItemRelationshipsStore((s) => s.groups);
  const allMemberships = useItemRelationshipsStore((s) => s.groupMemberships);
  const allEdges       = useItemRelationshipsStore((s) => s.edges);

  // ── Step state ─────────────────────────────────────────────────────────────
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // ── Step 1 state ───────────────────────────────────────────────────────────
  const [action, setAction] = useState<LineageAction>('retire');
  const [supersedeTargetId, setSupersedeTargetId] = useState('');
  const [splitTitles, setSplitTitles]   = useState<string[]>(['', '']);
  const [forkTitle, setForkTitle]       = useState('');
  const [mergeTargetIds, setMergeTargetIds] = useState<string[]>(['']);
  const [mergedTitle, setMergedTitle]   = useState('');

  // ── Step 2 state ───────────────────────────────────────────────────────────
  const [resolutionItems, setResolutionItems] = useState<ResolutionItem[]>([]);

  // ── Step 3 state ───────────────────────────────────────────────────────────
  const [rationale, setRationale] = useState('');

  // ── Shared ─────────────────────────────────────────────────────────────────
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      void fetchItems();
      void fetchFromSupabase();
    }
  }, [isOpen, fetchItems, fetchFromSupabase]);

  // Reset on open
  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setAction(initialAction);
    setSupersedeTargetId('');
    setSplitTitles(['', '']);
    setForkTitle('');
    setMergeTargetIds(['']);
    setMergedTitle('');
    setRationale('');
    setError(null);
    setResolutionItems([]);
  }, [isOpen, initialAction]);

  // ── Derived: how many successors will be created? ──────────────────────────
  const successorCount = useMemo(() => {
    switch (action) {
      case 'retire': return 0;
      case 'supersede': return supersedeTargetId ? 1 : 0;
      case 'split': return splitTitles.filter((t) => t.trim()).length;
      case 'merge': return 1;
      case 'fork': return 1;
    }
  }, [action, supersedeTargetId, splitTitles]);

  // ── Step 1 validity ────────────────────────────────────────────────────────
  const step1Valid = useMemo(() => {
    switch (action) {
      case 'retire': return true;
      case 'supersede': return !!supersedeTargetId;
      case 'split': return splitTitles.filter((t) => t.trim()).length >= 2;
      case 'fork': return !!forkTitle.trim();
      case 'merge': return mergeTargetIds.some(Boolean) && !!mergedTitle.trim();
    }
  }, [action, supersedeTargetId, splitTitles, forkTitle, mergeTargetIds, mergedTitle]);

  // ── Step 2 resolution items ────────────────────────────────────────────────
  const computedItems = useMemo(
    () =>
      computeResolutionItems(
        action,
        sourceItemId,
        allGroups,
        allMemberships,
        allEdges,
        successorCount,
        getItemById,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [action, sourceItemId, allGroups, allMemberships, allEdges, successorCount],
  );

  const hasBlockers   = computedItems.some((i) => i.isBlocking && i.defaultDisposition === null);
  const needsStep2    = action !== 'fork' && computedItems.length > 0;

  const totalSteps    = needsStep2 ? 3 : 2;
  const STEP_LABELS   = ['Action', 'Resolve', 'Confirm'].slice(0, totalSteps);

  // Sync resolution items from computed when entering step 2
  const handleGoToStep2 = useCallback(() => {
    setResolutionItems(computedItems.map((i) => ({ ...i })));
    setStep(2);
  }, [computedItems]);

  const handleGoToStep3 = useCallback(() => {
    setStep(3);
  }, []);

  // Step 2 completion check: all non-blocking items must have a disposition
  const step2Valid = useMemo(() => {
    if (hasBlockers) return false;
    return resolutionItems
      .filter((i) => !(i.isBlocking && i.defaultDisposition === null))
      .every((i) => i.disposition !== null);
  }, [resolutionItems, hasBlockers]);

  const handleDispositionChange = useCallback(
    (id: string, disposition: DispositionCode) => {
      setResolutionItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, disposition } : i)),
      );
    },
    [],
  );

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);

    // We use a stable UUID for resolution records even when the store doesn't
    // return the lineage event ID directly. The resolution records are linked
    // by this ID so successors can query what was transferred to them.
    const resolutionBatchId = generateUUID();
    let successorItemIds: string[] = [];

    try {
      if (action === 'retire') {
        const result = await retireItem(sourceItemId, rationale.trim() || undefined);
        if (!result.success) { setError(result.error || 'Failed to retire item'); return; }
        await createClosureEvent({ itemId: sourceItemId, closureType: 'retired_closed', createdAt: new Date() });

      } else if (action === 'supersede') {
        if (!supersedeTargetId) { setError('Select the replacement item'); return; }
        const result = await supersedeItem(sourceItemId, supersedeTargetId, rationale.trim() || undefined);
        if (!result.success) { setError(result.error || 'Failed to supersede item'); return; }
        await createClosureEvent({ itemId: sourceItemId, closureType: 'superseded_closed', pointerItemId: supersedeTargetId, createdAt: new Date() });
        successorItemIds = [supersedeTargetId];

      } else if (action === 'split') {
        const validTitles = splitTitles.map((t) => t.trim()).filter(Boolean);
        if (validTitles.length < 2) { setError('Provide at least two titles'); return; }
        const result = await splitItem(sourceItemId, validTitles, rationale.trim() || undefined);
        if (!result.success) { setError(result.error || 'Failed to split item'); return; }
        successorItemIds = result.childIds ?? [];

      } else if (action === 'fork') {
        if (!forkTitle.trim()) { setError('Provide a title for the fork'); return; }
        const result = await forkItem(sourceItemId, forkTitle.trim(), rationale.trim() || undefined);
        if (!result.success) { setError(result.error || 'Failed to fork item'); return; }
        successorItemIds = result.forkId ? [result.forkId] : [];

      } else if (action === 'merge') {
        const filledTargetIds = mergeTargetIds.filter(Boolean);
        if (filledTargetIds.length === 0) { setError('Select at least one other item'); return; }
        if (!mergedTitle.trim()) { setError('Provide a title for the merged item'); return; }
        const allSourceIds = [sourceItemId, ...filledTargetIds];
        const result = await mergeItems(allSourceIds, mergedTitle.trim(), rationale.trim() || undefined);
        if (!result.success) { setError(result.error || 'Failed to merge items'); return; }
        successorItemIds = result.mergedId ? [result.mergedId] : [];
      }

      // Apply relationship resolutions
      if (resolutionItems.length > 0) {
        const resResult = await applyResolutions({
          lineageEventId: resolutionBatchId,
          sourceItemId,
          successorItemIds,
          resolutionItems,
        });
        if (resResult.errors.length > 0) {
          console.warn('[ProvenanceActionModal] Some resolutions failed:', resResult.errors);
        }
      }

      onComplete?.();
      onClose();

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const sourceItem = getItemById(sourceItemId);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-lg max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="px-6 py-4 border-b flex-shrink-0 space-y-3">
          <div className="flex items-start justify-between gap-3 min-w-0">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-[var(--text-pri)]">Provenance Actions</h2>
              {sourceItem && (
                <p className="text-xs text-[var(--text-ter)] truncate mt-0.5">
                  {sourceItem.title}
                </p>
              )}
            </div>
            <button onClick={onClose} className="p-2 hover:bg-[var(--hover-tint)] transition-colors flex-shrink-0 -mr-1 -mt-1">
              <X className="h-5 w-5 text-[var(--text-ter)]" />
            </button>
          </div>
          <StepIndicator
            currentStep={step}
            totalSteps={totalSteps}
            stepLabels={STEP_LABELS}
          />
        </div>

        {/* Scrollable body */}
        <div className="p-6 space-y-5 overflow-y-auto flex-1">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* ── STEP 1: Choose action ─────────────────────────────────────── */}
          {step === 1 && (
            <>
              <div>
                <label className="block text-sm font-semibold text-[var(--text-pri)] mb-2">Action</label>
                <div className="space-y-2">
                  {ACTION_META.map((a) => {
                    const Icon = a.icon;
                    const selected = action === a.value;
                    return (
                      <button
                        key={a.value}
                        type="button"
                        onClick={() => { setAction(a.value); setError(null); }}
                        className={`w-full p-3  border-2 text-left flex items-start gap-3 transition-colors ${
                          selected ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--rule)] hover:border-[var(--rule)] bg-[var(--bg-raised)]'
                        }`}
                      >
                        <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${selected ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`} />
                        <div>
                          <div className={`font-medium text-sm ${selected ? 'text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}>{a.label}</div>
                          <div className="text-xs text-[var(--text-ter)] mt-0.5">{a.description}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Action-specific fields */}
              {action === 'supersede' && (
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                    Replacement item <span className="text-[var(--coral)]">*</span>
                  </label>
                  <SearchableItemPicker
                    value={supersedeTargetId || undefined}
                    onChange={(id) => setSupersedeTargetId(id ?? '')}
                    excludeId={sourceItemId}
                    label=""
                    placeholder="Search for the item that replaces this one…"
                    lifecycleStates={['item']}
                    allowEmpty={true}
                    emptyLabel="No selection"
                  />
                </div>
              )}

              {action === 'split' && (
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                    New item titles <span className="text-[var(--coral)]">*</span>
                    <span className="font-normal text-[var(--text-ter)] ml-1">(at least 2)</span>
                  </label>
                  <div className="space-y-2">
                    {splitTitles.map((title, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={title}
                          onChange={(e) => {
                            const next = [...splitTitles];
                            next[i] = e.target.value;
                            setSplitTitles(next);
                          }}
                          placeholder={`Child item ${i + 1}`}
                          className="flex-1 border border-[var(--rule)] px-3 py-2 text-sm focus:border-[var(--slate)] focus:ring-1 focus:ring-purple-400 focus:outline-none"
                        />
                        {splitTitles.length > 2 && (
                          <button type="button" onClick={() => setSplitTitles(splitTitles.filter((_, j) => j !== i))} className="text-[var(--text-ter)] hover:text-[var(--coral)] p-1 transition-colors">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={() => setSplitTitles([...splitTitles, ''])} className="mt-2 inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors">
                    <Plus className="h-3 w-3" /> Add another
                  </button>
                </div>
              )}

              {action === 'fork' && (
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Fork title <span className="text-[var(--coral)]">*</span></label>
                  <input
                    type="text"
                    value={forkTitle}
                    onChange={(e) => setForkTitle(e.target.value)}
                    placeholder="Name for the forked variant…"
                    className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:border-[var(--slate)] focus:ring-1 focus:ring-purple-400 focus:outline-none"
                  />
                  <p className="mt-1.5 text-xs text-[var(--text-ter)]">Original item and its relationships are unaffected. Fork starts with no relationships.</p>
                </div>
              )}

              {action === 'merge' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Items to merge with <span className="text-[var(--coral)]">*</span></label>
                    <div className="space-y-2">
                      {mergeTargetIds.map((targetId, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="flex-1">
                            <SearchableItemPicker
                              value={targetId || undefined}
                              onChange={(id) => { const next = [...mergeTargetIds]; next[i] = id ?? ''; setMergeTargetIds(next); }}
                              excludeId={sourceItemId}
                              label=""
                              placeholder="Search for an item to merge…"
                              lifecycleStates={['item']}
                              allowEmpty={false}
                              filterFn={(item) => !mergeTargetIds.filter((_, j) => j !== i).includes(item.id)}
                            />
                          </div>
                          {mergeTargetIds.length > 1 && (
                            <button type="button" onClick={() => setMergeTargetIds(mergeTargetIds.filter((_, j) => j !== i))} className="text-[var(--text-ter)] hover:text-[var(--coral)] p-1 transition-colors flex-shrink-0">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button type="button" onClick={() => setMergeTargetIds([...mergeTargetIds, ''])} className="mt-2 inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)]">
                      <Plus className="h-3 w-3" /> Add another item
                    </button>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Title for merged item <span className="text-[var(--coral)]">*</span></label>
                    <input
                      type="text"
                      value={mergedTitle}
                      onChange={(e) => setMergedTitle(e.target.value)}
                      placeholder="Name for the new combined item…"
                      className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:border-[var(--slate)] focus:ring-1 focus:ring-purple-400 focus:outline-none"
                    />
                  </div>
                </div>
              )}

              {/* Hard blocker warning — canon 07B §6, Build Contract §5 Readiness UI honesty */}
              {hasBlockers && action !== 'fork' && (
                <div className="p-3.5 bg-[var(--coral-dim)] border border-[var(--coral)] space-y-2">
                  <div className="flex items-start gap-2">
                    <Ban className="h-4 w-4 text-[var(--coral)] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-[var(--coral)]">Can't do this yet — the item is in a package</p>
                      <p className="text-xs text-[var(--coral)] mt-0.5">
                        This item is in a package, and a package only holds while every member is still active.
                        Take this item out of the package, or dissolve the package, before you retire, replace,
                        split or merge it.
                      </p>
                    </div>
                  </div>
                  <div className="pl-6 space-y-1">
                    {computedItems
                      .filter((i) => i.isBlocking && i.defaultDisposition === null && i.typeLabel === 'Package')
                      .map((i) => (
                        <div key={i.id} className="flex items-center gap-2 text-xs text-[var(--coral)]">
                          <span className="font-medium">Package:</span>
                          <span>{i.otherItemIds.length} other member{i.otherItemIds.length !== 1 ? 's' : ''}</span>
                          <span className="text-[var(--coral)]">—</span>
                          <a
                            href="/prioritization/relationships/packages"
                            className="underline hover:text-[var(--coral)]"
                            onClick={onClose}
                          >
                            Go to Packages to dissolve →
                          </a>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Consequence warning (non-blocking actions) */}
              {action !== 'fork' && !hasBlockers && (
                <div className="p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-xs text-[var(--mustard)]">
                  {action === 'retire' && 'This item will be retired and archived. The action cannot be undone.'}
                  {action === 'supersede' && 'This item will be retired. Its relationships will be offered for transfer to the replacement.'}
                  {action === 'split' && 'The original item is retired. You will review relationship disposition in the next step.'}
                  {action === 'merge' && 'All selected items will be archived. The merged item inherits their relationships.'}
                </div>
              )}
            </>
          )}

          {/* ── STEP 2: Resolve relationships ─────────────────────────────── */}
          {step === 2 && needsStep2 && (
            <>
              <div>
                <h3 className="text-sm font-semibold text-[var(--text-pri)] mb-0.5">Resolve Relationships</h3>
                <p className="text-xs text-[var(--text-ter)]">
                  Declare what should happen to each active relationship on this item.
                  {hasBlockers && (
                    <span className="text-[var(--coral)] font-medium ml-1">
                      Blocked items must be resolved before you can proceed.
                    </span>
                  )}
                </p>
              </div>

              {resolutionItems.length === 0 ? (
                <div className="py-6 text-center text-sm text-[var(--text-ter)]">
                  <CheckCircle2 className="h-6 w-6 text-[var(--green)] mx-auto mb-2" />
                  No active relationships to resolve.
                </div>
              ) : (
                <div className="space-y-2">
                  {resolutionItems.map((item) => (
                    <ResolutionRow
                      key={item.id}
                      item={item}
                      onChange={handleDispositionChange}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── STEP 3 (or step 2 when no rels): Rationale + confirm ──────── */}
          {((step === 3 && needsStep2) || (step === 2 && !needsStep2)) && (
            <>
              <div>
                <h3 className="text-sm font-semibold text-[var(--text-pri)] mb-0.5">Confirm</h3>
                <p className="text-xs text-[var(--text-ter)]">
                  Review your choices, add a rationale, then confirm.
                </p>
              </div>

              {/* Summary of chosen action */}
              <div className="border border-[var(--rule)] p-3 bg-[var(--surface)] text-sm text-[var(--text-sec)]">
                <span className="font-medium capitalize text-[var(--text-pri)]">{action}</span>
                {action === 'supersede' && supersedeTargetId && (
                  <span className="ml-1.5">→ <span className="text-[var(--text-ter)]">{getItemById(supersedeTargetId)?.title ?? supersedeTargetId.slice(0, 12) + '…'}</span></span>
                )}
                {action === 'split' && (
                  <span className="ml-1.5 text-[var(--text-ter)]">into {splitTitles.filter((t) => t.trim()).length} items</span>
                )}
                {action === 'merge' && mergedTitle && (
                  <span className="ml-1.5">→ <span className="text-[var(--text-ter)]">"{mergedTitle}"</span></span>
                )}
                {action === 'fork' && forkTitle && (
                  <span className="ml-1.5">→ fork <span className="text-[var(--text-ter)]">"{forkTitle}"</span></span>
                )}
                {needsStep2 && resolutionItems.length > 0 && (
                  <div className="mt-1.5 text-xs text-[var(--text-ter)]">
                    {resolutionItems.filter((i) => i.disposition === 'transfer').length} relationship(s) will transfer ·{' '}
                    {resolutionItems.filter((i) => i.disposition === 'dissolve').length} will dissolve
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Rationale <span className="text-[var(--text-ter)] font-normal">(optional)</span>
                </label>
                <textarea
                  value={rationale}
                  onChange={(e) => setRationale(e.target.value)}
                  placeholder="Why is this change being made?"
                  rows={3}
                  className="w-full px-3 py-2 border border-[var(--rule)] resize-none text-sm focus:border-[var(--slate)] focus:ring-1 focus:ring-purple-400 focus:outline-none"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t bg-[var(--surface)] flex-shrink-0">
          {/* Back */}
          <div>
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep((s) => (s - 1) as 1 | 2 | 3)}
                disabled={isSubmitting}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-[var(--text-sec)] font-medium hover:bg-[var(--hover-tint)] transition-colors disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
            )}
          </div>

          {/* Forward / Submit */}
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-[var(--text-sec)] font-medium hover:bg-[var(--hover-tint)] transition-colors disabled:opacity-50 text-sm"
            >
              Cancel
            </button>

            {/* Step 1 → step 2 or confirm */}
            {step === 1 && (
              <button
                type="button"
                disabled={!step1Valid || hasBlockers}
                onClick={() => (needsStep2 ? handleGoToStep2() : setStep(2))}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--slate)] text-white font-medium hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                {needsStep2 ? 'Resolve relationships' : 'Continue'}
                <ChevronRight className="h-4 w-4" />
              </button>
            )}

            {/* Step 2 (resolution) → step 3 */}
            {step === 2 && needsStep2 && (
              <button
                type="button"
                disabled={!step2Valid}
                onClick={handleGoToStep3}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--slate)] text-white font-medium hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                Continue
                <ChevronRight className="h-4 w-4" />
              </button>
            )}

            {/* Final confirm */}
            {((step === 3 && needsStep2) || (step === 2 && !needsStep2)) && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white font-medium hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {isSubmitting ? 'Processing…' : `Confirm ${action}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
